/**
 * KOAnimation Component
 *
 * Fire Emblem Heroes-style KO animation when a Pokemon defeats an opponent.
 * Sequence: Freeze battle -> Zoom on attacker (2-3x) -> Victory animation ->
 * Focus on defeated Pokemon with hit effects -> Grey out defeated Pokemon
 *
 * Special case for ties: Show attacker victory (fakeout) -> Reveal counter-attack -> Both KO'd
 */

import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { generatePokemonSprite, getEvolutionFormForGrade } from '../utils/gameUtils';
import { MOVES } from '../shared/gameData';
import { TYPE_COLORS } from './TypeIcon';

const KOAnimation = ({
  koData,  // { target, attacker, isTie, koMove }
  playerPokemon,  // { name, primaryType, currentHP, stats }
  opponentPokemon,
  onComplete,  // callback to resume battle
  onSkip  // callback for skip functionality
}) => {
  const [phase, setPhase] = useState('zoom-attacker');
  // Phases for normal KO: 'zoom-attacker' -> 'hit-target' -> 'grey-out' -> 'complete'
  // Phases for tie: 'zoom-attacker' -> 'fakeout-pause' -> 'counter-reveal' -> 'both-ko' -> 'complete'

  // Determine which Pokemon is which based on koData
  const attackerPokemon = koData.attacker === 'player1' ? playerPokemon : opponentPokemon;
  const targetPokemon = koData.target === 'player1' ? playerPokemon : opponentPokemon;

  // Get move data for display
  const koMove = koData.koMove ? MOVES[koData.koMove] : null;
  const moveTypeColor = koMove ? (TYPE_COLORS[koMove.type] || '#A8A878') : '#A8A878';

  useEffect(() => {
    // Phase timing
    if (!koData.isTie) {
      // Normal KO sequence
      const timer1 = setTimeout(() => setPhase('hit-target'), 800);  // 0.8s zoom on attacker
      const timer2 = setTimeout(() => setPhase('grey-out'), 1300);   // 0.5s hit effects
      const timer3 = setTimeout(() => setPhase('complete'), 2000);   // 0.7s grey out

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    } else {
      // Tie KO sequence with fakeout
      const timer1 = setTimeout(() => setPhase('fakeout-pause'), 800);     // 0.8s zoom on "winner"
      const timer2 = setTimeout(() => setPhase('counter-reveal'), 1300);   // 0.5s fakeout
      const timer3 = setTimeout(() => setPhase('both-ko'), 1800);          // 0.5s counter animation
      const timer4 = setTimeout(() => setPhase('complete'), 2500);         // 0.7s both grey out

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
        clearTimeout(timer4);
      };
    }
  }, [koData.isTie]);

  useEffect(() => {
    if (phase === 'complete') {
      const completeTimer = setTimeout(onComplete, 300);
      return () => clearTimeout(completeTimer);
    }
  }, [phase, onComplete]);

  // Keyboard/click skip handler
  useEffect(() => {
    const handleSkip = (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        onSkip();
      }
    };

    document.addEventListener('keydown', handleSkip);
    return () => document.removeEventListener('keydown', handleSkip);
  }, [onSkip]);

  return (
    <AnimatePresence>
      {/* Fullscreen overlay */}
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 cursor-pointer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onSkip}
      >
        {/* Phase 1: Zoom on Attacker */}
        {phase === 'zoom-attacker' && (
          <>
            <motion.div
              className="relative"
              initial={{ scale: 1 }}
              animate={{ scale: 2.5, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <motion.div
                animate={{
                  rotate: [0, -5, 5, -5, 0],
                  filter: ['brightness(1)', 'brightness(1.5)', 'brightness(1)']
                }}
                transition={{ duration: 0.4, repeat: 1 }}
              >
                {generatePokemonSprite(
                  attackerPokemon.primaryType,
                  getEvolutionFormForGrade(attackerPokemon.name, attackerPokemon.stats),
                  200
                )}
              </motion.div>
              {/* Victory flash effect */}
              <motion.div
                className="absolute inset-0 bg-white rounded-full pointer-events-none"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: [0, 0.5, 0], scale: [0.8, 1.2, 1.5] }}
                transition={{ duration: 0.5 }}
              />
            </motion.div>
            {/* Move name display - Fire Emblem style - FIXED position on screen */}
            <motion.div
              className="absolute bottom-1/4 left-1/2 -translate-x-1/2 pointer-events-none z-50 max-w-[90vw] sm:max-w-none"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
            >
              <div
                className="relative text-2xl sm:text-4xl font-bold px-3 sm:px-6 py-2 sm:py-3 rounded-lg shadow-2xl whitespace-nowrap overflow-hidden"
                style={{
                  color: '#fff',
                  backgroundColor: moveTypeColor,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.8), -1px -1px 2px rgba(0,0,0,0.5)',
                  boxShadow: `0 0 30px ${moveTypeColor}, 0 0 60px ${moveTypeColor}80`,
                }}
              >
                {koData.koMove}
                {/* Animated shine effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
                  initial={{ x: '-100%' }}
                  animate={{ x: '200%' }}
                  transition={{
                    duration: 1.2,
                    ease: 'easeInOut',
                    repeat: Infinity,
                    repeatDelay: 0.8
                  }}
                />
              </div>
            </motion.div>
          </>
        )}

        {/* Phase 2: Hit Target (screen shake + particles) */}
        {phase === 'hit-target' && (
          <motion.div
            className="relative"
            animate={{
              x: [-30, 30, -30, 30, -20, 20, 0],
              scale: [1, 1.1, 0.9, 1.05, 1]
            }}
            transition={{ duration: 0.4 }}
          >
            {/* Screen flash */}
            <motion.div
              className="fixed inset-0 bg-red-500 mix-blend-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.8, 0.3, 0] }}
              transition={{ duration: 0.3 }}
            />

            {/* Target Pokemon with hit effect */}
            <motion.div
              animate={{
                rotate: [0, 15, -15, 10, -10, 0],
                filter: ['brightness(1)', 'brightness(3)', 'brightness(1)', 'brightness(2.5)', 'brightness(1)']
              }}
              transition={{ duration: 0.4 }}
            >
              {generatePokemonSprite(
                targetPokemon.primaryType,
                getEvolutionFormForGrade(targetPokemon.name, targetPokemon.stats),
                200
              )}
            </motion.div>

            {/* Particle burst */}
            <KOParticleBurst />
          </motion.div>
        )}

        {/* Phase 3: Grey Out */}
        {phase === 'grey-out' && (
          <motion.div
            initial={{ scale: 1.1 }}
            animate={{
              scale: 0.7,
              opacity: 0.3,
              filter: 'grayscale(100%)'
            }}
            transition={{ duration: 0.6, ease: 'easeIn' }}
          >
            {generatePokemonSprite(
              targetPokemon.primaryType,
              getEvolutionFormForGrade(targetPokemon.name, targetPokemon.stats),
              200
            )}
          </motion.div>
        )}

        {/* TIE-SPECIFIC PHASES */}
        {phase === 'fakeout-pause' && (
          <motion.div
            className="relative"
            initial={{ scale: 1 }}
            animate={{ scale: 2.5 }}
            transition={{ duration: 0.6 }}
          >
            {generatePokemonSprite(
              attackerPokemon.primaryType,
              getEvolutionFormForGrade(attackerPokemon.name, attackerPokemon.stats),
              200
            )}
            {/* Fake victory glow */}
            <motion.div
              className="absolute inset-0 bg-yellow-300 rounded-full mix-blend-overlay"
              animate={{ opacity: [0, 0.5, 0.3] }}
              transition={{ duration: 0.5, repeat: Infinity }}
            />
          </motion.div>
        )}

        {phase === 'counter-reveal' && (
          <>
            {/* Show attacker getting hit instead */}
            <motion.div
              className="relative"
              initial={{ scale: 2.5 }}
              animate={{
                x: [-20, 20, -20, 20, 0],
                rotate: [0, -10, 10, -10, 0]
              }}
              transition={{ duration: 0.3 }}
            >
              {generatePokemonSprite(
                attackerPokemon.primaryType,
                getEvolutionFormForGrade(attackerPokemon.name, attackerPokemon.stats),
                200
              )}
              <motion.div
                className="fixed inset-0 bg-red-600 mix-blend-overlay"
                animate={{ opacity: [0, 0.7, 0] }}
                transition={{ duration: 0.2 }}
              />
            </motion.div>
            {/* "COUNTER!" text flash */}
            <motion.div
              className="absolute top-20 text-white text-4xl font-bold"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              COUNTER!
            </motion.div>
          </>
        )}

        {phase === 'both-ko' && (
          <div className="flex gap-12">
            <motion.div
              animate={{
                opacity: 0.3,
                scale: 0.6,
                filter: 'grayscale(100%)'
              }}
              transition={{ duration: 0.5 }}
            >
              {generatePokemonSprite(
                playerPokemon.primaryType,
                getEvolutionFormForGrade(playerPokemon.name, playerPokemon.stats),
                150
              )}
            </motion.div>
            <motion.div
              animate={{
                opacity: 0.3,
                scale: 0.6,
                filter: 'grayscale(100%)'
              }}
              transition={{ duration: 0.5 }}
            >
              {generatePokemonSprite(
                opponentPokemon.primaryType,
                getEvolutionFormForGrade(opponentPokemon.name, opponentPokemon.stats),
                150
              )}
            </motion.div>
          </div>
        )}

        {/* Skip indicator */}
        <motion.div
          className="absolute bottom-8 text-white/60 text-sm"
          animate={{ opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          Press SPACE or Click to Skip
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Particle burst component
const KOParticleBurst = () => {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    angle: (360 / 20) * i,
    distance: 100 + Math.random() * 50,
    duration: 0.5 + Math.random() * 0.3
  }));

  return (
    <div className="absolute inset-0 pointer-events-none">
      {particles.map(p => (
        <motion.div
          key={p.id}
          className="absolute top-1/2 left-1/2 w-3 h-3 bg-red-500 rounded-full"
          initial={{
            x: 0,
            y: 0,
            opacity: 1,
            scale: 1
          }}
          animate={{
            x: Math.cos(p.angle * Math.PI / 180) * p.distance,
            y: Math.sin(p.angle * Math.PI / 180) * p.distance,
            opacity: 0,
            scale: 0.3
          }}
          transition={{ duration: p.duration }}
        />
      ))}
    </div>
  );
};

export default KOAnimation;
